import React, { useEffect, useState, useCallback } from 'react';
import './StampPop.css';

const formatDuration = (duration) => {
	if (duration == null) {
		return null;
	}

	if (typeof duration === 'number') {
		if (Number.isNaN(duration) || duration < 0) {
			return null;
		}
		const hours = Math.floor(duration / 60);
		const minutes = Math.round(duration % 60);
		if (hours > 0) {
			return `${hours}h ${minutes}m`;
		}
		return `${minutes}m`;
	}

	if (typeof duration === 'string' && duration.trim().length > 0) {
		return duration;
	}

	return null;
};

const formatGeneratedAt = (value) => {
	if (!value) {
		return null;
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return null;
	}

	try {
		return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
	} catch (error) {
		console.error('Failed to format generated timestamp:', error);
		return date.toString();
	}
};

const parseTimestampToSeconds = (value) => {
	if (typeof value !== 'string') {
		return null;
	}

	const trimmed = value.trim();
	if (!trimmed) {
		return null;
	}

	if (trimmed.includes(':')) {
		const parts = trimmed.split(':').map((segment) => segment.trim());
		if (parts.some((segment) => segment === '' || Number.isNaN(Number(segment)))) {
			return null;
		}
		return parts.reduce((total, segment) => (total * 60) + Number(segment), 0);
	}

	const durationPattern = /^(?:(\d+)h)?\s*(?:(\d+)m)?\s*(?:(\d+)s?)?$/i;
	const match = trimmed.match(durationPattern);
	if (match) {
		const hours = Number(match[1] ?? 0);
		const minutes = Number(match[2] ?? 0);
		const seconds = Number(match[3] ?? 0);
		if ([hours, minutes, seconds].some((num) => Number.isNaN(num))) {
			return null;
		}
		const totalSeconds = (hours * 3600) + (minutes * 60) + seconds;
		return totalSeconds >= 0 ? totalSeconds : null;
	}

	const numericValue = Number(trimmed);
	return Number.isNaN(numericValue) || numericValue < 0 ? null : numericValue;
};

const buildVideoTimestampUrl = (baseUrl, seconds) => {
	if (!baseUrl || seconds == null || seconds < 0) {
		return baseUrl;
	}

	try {
		const url = new URL(baseUrl);
		const hostname = url.hostname.toLowerCase();
		const secondsString = seconds.toString();

		if (hostname.includes('youtube.com')) {
			if (url.pathname.startsWith('/embed/')) {
				url.searchParams.set('start', secondsString);
				url.searchParams.delete('t');
			} else {
				url.searchParams.set('t', secondsString);
				url.searchParams.delete('start');
			}
		} else if (hostname.includes('youtu.be')) {
			url.searchParams.set('t', secondsString);
		} else {
			url.searchParams.set('t', secondsString);
		}

		return url.toString();
	} catch (error) {
		const separator = baseUrl.includes('?') ? '&' : '?';
		return `${baseUrl}${separator}t=${seconds}`;
	}
};

const StampPop = ({ isOpen, onClose, data }) => {
	const [copyStatus, setCopyStatus] = useState(null);
	const videoUrl = data?.url;

	const handleTimestampClick = useCallback((timeValue) => {
		if (typeof window === 'undefined' || !videoUrl) {
			return;
		}

		const seconds = parseTimestampToSeconds(timeValue);
		const targetUrl = seconds != null ? buildVideoTimestampUrl(videoUrl, seconds) : videoUrl;

		try {
			window.open(targetUrl, '_blank', 'noopener,noreferrer');
		} catch (error) {
			console.error('Failed to open timestamp link:', error);
		}
	}, [videoUrl]);

	const closeModal = useCallback(() => {
		if (typeof onClose === 'function') {
			onClose();
		}
	}, [onClose]);

	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}

		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				closeModal();
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen, closeModal]);

	useEffect(() => {
		if (isOpen) {
			document.body.classList.add('stamp-pop-lock');
		} else {
			document.body.classList.remove('stamp-pop-lock');
		}

		return () => {
			document.body.classList.remove('stamp-pop-lock');
		};
	}, [isOpen]);

	useEffect(() => {
		if (isOpen) {
			setCopyStatus(null);
		}
	}, [isOpen]);

	if (!isOpen || !data) {
		return null;
	}

	const handleCopy = async () => {
		const textToCopy = data.timestampsString || data.timestampsList?.join('\n');

		if (!textToCopy) {
			setCopyStatus({ type: 'error', message: 'Nothing to copy yet.' });
			return;
		}

		if (!navigator.clipboard) {
			setCopyStatus({ type: 'error', message: 'Clipboard unavailable.' });
			return;
		}

		try {
			await navigator.clipboard.writeText(textToCopy);
			setCopyStatus({ type: 'success', message: 'Copied to clipboard.' });
		} catch (error) {
			console.error('Failed to copy timestamps:', error);
			setCopyStatus({ type: 'error', message: 'Copy failed. Try again.' });
		}
	};

	const durationLabel = formatDuration(data.videoDuration);
	const generatedLabel = formatGeneratedAt(data.generatedAt);
	const timestampsList = Array.isArray(data.timestampsList) ? data.timestampsList : [];

	return (
		<div className="stamp-pop-overlay" role="dialog" aria-modal="true" aria-labelledby="stamp-pop-title" onClick={(event) => {
			if (event.target === event.currentTarget) {
				closeModal();
			}
		}}>
			<div className="stamp-pop-card">
				<header className="stamp-pop-header">
					<div>
						<p className="stamp-pop-tag">bumpups</p>
						<h3 className="stamp-pop-title" id="stamp-pop-title">AI-generated timestamps</h3>
						{data.url && (
							<a
								className="stamp-pop-link"
								href={data.url}
								target="_blank"
								rel="noopener noreferrer"
							>
								View source video
							</a>
						)}
					</div>
					<button type="button" className="stamp-pop-close" onClick={closeModal} aria-label="Close timestamps popup">
						✕
					</button>
				</header>

				<section className="stamp-pop-meta">
					{data.model && (
						<span className="stamp-pop-pill">Model: {data.model}</span>
					)}
					{data.language && (
						<span className="stamp-pop-pill">Language: {data.language}</span>
					)}
					{data.timestampsStyle && (
						<span className="stamp-pop-pill">Style: {data.timestampsStyle}</span>
					)}
					{durationLabel && (
						<span className="stamp-pop-pill">Duration: {durationLabel}</span>
					)}
				</section>

				<div className="stamp-pop-body">
					{(data.thumbnail || data.title || generatedLabel) && (
						<div className="stamp-pop-video">
							<div className="stamp-pop-video-thumb">
								{data.thumbnail ? (
									<img src={data.thumbnail} alt={data.title ? `Thumbnail for ${data.title}` : 'Video thumbnail'} />
								) : (
									<div className="stamp-pop-video-thumb-fallback" aria-hidden="true">
										No thumbnail
									</div>
								)}
							</div>
							<div className="stamp-pop-video-info">
								{data.title && (
									<p className="stamp-pop-video-title">{data.title}</p>
								)}
								{generatedLabel && (
									<p className="stamp-pop-generated">Generated {generatedLabel}</p>
								)}
							</div>
						</div>
					)}
					{timestampsList.length > 0 ? (
						<ul className="stamp-pop-list">
							{timestampsList.map((entry, index) => {
								const [timeSegment, ...labelSegments] = entry.split(' - ');
								const rawTime = (timeSegment ?? '').trim();
								const timeValue = rawTime || entry;
								const labelText = labelSegments.length > 0 ? labelSegments.join(' - ').trim() : entry;
								const ariaLabel = labelText && labelText !== timeValue
									? `Open video at ${timeValue} - ${labelText}`
									: `Open video at ${timeValue}`;

								return (
									<li
										key={`${index}-${entry}`}
										className="stamp-pop-list-item"
										onClick={() => handleTimestampClick(timeValue)}
										onKeyDown={(event) => {
											if (event.key === 'Enter' || event.key === ' ') {
												event.preventDefault();
												handleTimestampClick(timeValue);
											}
										}}
										role="button"
										tabIndex={0}
										aria-label={ariaLabel}
									>
										<span className="stamp-pop-time">{timeValue}</span>
										<span className="stamp-pop-label">{labelText}</span>
									</li>
								);
							})}
						</ul>
					) : (
						<p className="stamp-pop-empty" role="status">No timestamps available for this entry.</p>
					)}
				</div>

				<footer className="stamp-pop-footer">
					<button type="button" className="stamp-pop-copy" onClick={handleCopy}>
						Copy timestamps
					</button>
					<button type="button" className="stamp-pop-secondary" onClick={closeModal}>
						Close
					</button>
				</footer>
				{copyStatus && (
					<p className={`stamp-pop-feedback ${copyStatus.type === 'error' ? 'error' : 'success'}`} role="status">
						{copyStatus.message}
					</p>
				)}
			</div>
		</div>
	);
};

export default StampPop;
